import { useCallback, useMemo } from "react";

import { UNIT_IDS, UnitCategory, UnitSystem } from "types/units";

import { useEvaUnitCore } from "./useEvaUnitCore";
import { useUnitConversion } from "./useUnitConversion";
import useUserUnit from "./useUserUnit";

export function useTypeWellUnitConversion() {
  const unitType = useUserUnit();
  const { isReady, convertUnitToUnit } = useEvaUnitCore();

  const lengthConversion = useUnitConversion(UnitCategory.Length);

  const convertProppantIntensityToDisplay = useCallback(
    (valueInTonnePerMeter: number): number => {
      if (!valueInTonnePerMeter || !isReady) return valueInTonnePerMeter;

      if (unitType === UnitSystem.Imperial) {
        return convertUnitToUnit(
          UNIT_IDS.TONNE_PER_METER,
          UNIT_IDS.LB_PER_FOOT,
          valueInTonnePerMeter,
          3
        );
      }
      return valueInTonnePerMeter;
    },
    [isReady, unitType, convertUnitToUnit]
  );

  const convertProppantIntensityToSource = useCallback(
    (valueInDisplayUnit: number): number => {
      if (!valueInDisplayUnit || !isReady) return valueInDisplayUnit;

      if (unitType === UnitSystem.Imperial) {
        return convertUnitToUnit(
          UNIT_IDS.LB_PER_FOOT,
          UNIT_IDS.TONNE_PER_METER,
          valueInDisplayUnit,
          6
        );
      }
      return valueInDisplayUnit;
    },
    [isReady, unitType, convertUnitToUnit]
  );

  return useMemo(
    () => ({
      isReady: lengthConversion.isReady,

      convertLengthToDisplay: (value: number) => lengthConversion.convert(value, 0),
      convertLengthToSource: (value: number) => lengthConversion.convertBack(value, 2),

      convertProppantIntensityToDisplay,
      convertProppantIntensityToSource,

      lengthUnit: lengthConversion.getDisplayUnit(),
      proppantIntensityUnit:
        unitType === UnitSystem.Imperial ? UNIT_IDS.LB_PER_FOOT : UNIT_IDS.TONNE_PER_METER
    }),
    [
      lengthConversion,
      convertProppantIntensityToDisplay,
      convertProppantIntensityToSource,
      unitType
    ]
  );
}
